import React from 'react';
import PropTypes from 'prop-types';
import { defineMessages, intlShape, FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import gql from 'graphql-tag';

import MediaCard from '@redbubble/design-system/react/MediaCard';
import Text from '@redbubble/design-system/react/Text';
import * as constants from '@redbubble/design-system/react/constants';

import { workLocation } from '../../lib/routing/routing';
import Favorite from '../Favorite';

const messages = defineMessages({
  artistAttribution: { defaultMessage: 'by {artistName}' },
  productCount: {
    defaultMessage: '{count} products',
    description: 'Number of products for work items in recently viewed.'
  }
});

const WorkCard = ({
  item,
  toggleFavorite,
  favorited,
  isLoggedIn,
  openSignUpModal,
  locale,
  intl,
  showFavorite,
  urlRef,
  transparent,
  showProductCount
}) => {
  const {
    title,
    artistShopDisplayName,
    imageUrl,
    workId,
    availableProductCount
  } = item;

  const handleNonMemberFavorite = () => openSignUpModal(imageUrl);

  const handleFavoriteClick = isLoggedIn
    ? toggleFavorite
    : handleNonMemberFavorite;

  const cardTitle = `${title}`;
  const artistAttribution = intl.formatMessage(messages.artistAttribution, {
    artistName: artistShopDisplayName
  });

  return (
    <MediaCard
      title={cardTitle}
      caption={artistAttribution}
      element={Link}
      to={workLocation({
        workId,
        locale,
        search: urlRef ? `?ref=${urlRef}` : null
      })}
      imageRatio={constants.RATIO_SQUARE}
      imageCover
      imageSrc={imageUrl}
      transparent={transparent}
      primaryActionSlot={
        showFavorite ? (
          <Favorite favorited={favorited} onClick={handleFavoriteClick} />
        ) : null
      }
    >
      {showProductCount && (
        <FormattedMessage
          {...messages.productCount}
          values={{ count: availableProductCount }}
        >
          {text => (
            <Text type="body2">
              <strong>{text}</strong>
            </Text>
          )}
        </FormattedMessage>
      )}
    </MediaCard>
  );
};

WorkCard.displayName = 'WorkCard';

WorkCard.fragment = gql`
  fragment WorkCard on Work {
    title
    artistName
    artistShopDisplayName
    imageUrl
    workId
    availableProductCount
  }
`;

WorkCard.propTypes = {
  item: PropTypes.shape({
    title: PropTypes.string.isRequired,
    artistName: PropTypes.string.isRequired,
    imageUrl: PropTypes.string.isRequired
  }).isRequired,
  toggleFavorite: PropTypes.func,
  favorited: PropTypes.bool,
  showFavorite: PropTypes.bool.isRequired,
  isLoggedIn: PropTypes.bool,
  openSignUpModal: PropTypes.func,
  intl: intlShape.isRequired,
  urlRef: PropTypes.string,
  transparent: PropTypes.bool,
  showProductCount: PropTypes.bool
};

WorkCard.defaultProps = {
  favorited: false,
  toggleFavorite: () => {},
  isLoggedIn: false,
  openSignUpModal: () => {},
  urlRef: null,
  transparent: false,
  showProductCount: true
};

export default WorkCard;
